import type { IconName } from '../types';

export type UiIcon =
  | IconName
  | 'play' | 'pause' | 'prev' | 'next' | 'more' | 'list' | 'close' | 'today' | 'explore' | 'profile'
  | 'check' | 'chevron' | 'calendar' | 'external' | 'volume' | 'back';

// One line-icon set: 24px grid, 1.5px stroke, round caps and joins.
const paths: Record<UiIcon, JSX.Element> = {
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4" />
    </>
  ),
  lotus: (
    <>
      <path d="M12 19c-3-1.5-4.5-4.2-4.5-7.5C9.6 12 11 13.2 12 15c1-1.8 2.4-3 4.5-3.5 0 3.3-1.5 6-4.5 7.5z" />
      <path d="M12 15c-1.2-2.6-1.2-5.6 0-9 1.2 3.4 1.2 6.4 0 9z" />
      <path d="M4 14c1.2 2.8 3.6 4.5 8 5 4.4-.5 6.8-2.2 8-5" />
    </>
  ),
  wind: (
    <>
      <path d="M3 9h10a2.5 2.5 0 1 0-2.5-2.5" />
      <path d="M3 13h15a2.5 2.5 0 1 1-2.5 2.5" />
      <path d="M3 17h7" />
    </>
  ),
  bee: (
    <>
      <ellipse cx="12" cy="14" rx="4.5" ry="5" />
      <path d="M8 13.5h8M8.5 16.5h7" />
      <path d="M9 9.5C7 6.5 5 6.5 4.5 8c-.4 1.4 1.6 2.6 4 2.5M15 9.5c2-3 4-3 4.5-1.5.4 1.4-1.6 2.6-4 2.5" />
      <path d="M10 5l1.2 1.6M14 5l-1.2 1.6" />
    </>
  ),
  om: (
    <text x="12" y="18" textAnchor="middle" fontSize="17" fill="currentColor" stroke="none">
      ॐ
    </text>
  ),
  moon: <path d="M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5z" />,
  music: (
    <>
      <path d="M9 17V6l10-2v11" />
      <circle cx="6.5" cy="17.5" r="2.5" />
      <circle cx="16.5" cy="15.5" r="2.5" />
    </>
  ),
  bell: (
    <>
      <path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15z" />
      <path d="M10 20.5a2 2 0 0 0 4 0" />
    </>
  ),
  book: (
    <>
      <path d="M5 4.5h10.5A2.5 2.5 0 0 1 18 7v12.5H7.5A2.5 2.5 0 0 1 5 17z" />
      <path d="M5 17a2.5 2.5 0 0 1 2.5-2.5H18" />
    </>
  ),
  timer: (
    <>
      <circle cx="12" cy="13.5" r="7" />
      <path d="M12 9.5v4l2.5 1.5M9.5 3h5" />
    </>
  ),
  play: <path d="M8 5.5v13l11-6.5z" />,
  pause: <path d="M8 5.5v13M16 5.5v13" />,
  prev: <path d="M17 5.5v13L8 12zM6 5.5v13" />,
  next: <path d="M7 5.5v13l9-6.5zM18 5.5v13" />,
  more: (
    <>
      <circle cx="5.5" cy="12" r="1.2" fill="currentColor" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
      <circle cx="18.5" cy="12" r="1.2" fill="currentColor" />
    </>
  ),
  list: <path d="M8 6.5h11M8 12h11M8 17.5h11M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  today: (
    <>
      <circle cx="12" cy="14" r="4" />
      <path d="M12 5v2M5.5 8.5L7 10M18.5 8.5L17 10M3 14h2M19 14h2" />
    </>
  ),
  explore: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M15.5 8.5l-2 5-5 2 2-5z" />
    </>
  ),
  profile: (
    <>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5 20c.6-3.6 3.4-5.5 7-5.5s6.4 1.9 7 5.5" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  chevron: <path d="M9 6l6 6-6 6" />,
  calendar: (
    <>
      <rect x="4" y="5.5" width="16" height="14.5" rx="3" />
      <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
    </>
  ),
  external: <path d="M14 5h5v5M19 5l-8 8M17 14v4a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 5 18V9a1.5 1.5 0 0 1 1.5-1.5H10" />,
  volume: (
    <>
      <path d="M5 9.5h3l4-3.5v12l-4-3.5H5z" />
      <path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" />
    </>
  ),
  back: <path d="M15 6l-6 6 6 6" />,
};

interface IconProps {
  name: UiIcon;
  size?: number;
  label?: string;
  className?: string;
}

export const Icon = ({ name, size = 24, label, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    role={label ? 'img' : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : true}
  >
    {paths[name]}
  </svg>
);
